import React, { useContext, useEffect, useState } from 'react';
import { Button, Icon, IconEnum } from '@investoro/core';
import styled from 'styled-components';
import PropertyValuationContext from 'utils/context/PropertyValuationContext';
import { PriceSearchValue } from 'utils/types/AiPredictorModels';

const RefreshButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
`;

const RefreshButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: transparent;
    border: 2px solid var(--color-primary);
    border-radius: 8px;
    color: var(--color-primary);
    font-size: var(--font-size-body-3);
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;

    &:hover:not(:disabled) {
        background: var(--color-primary);
        color: var(--color-white);
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

import RangeSlider from 'components/common/RangeSlider/RangeSlider';

const Container = styled.div`
    background-color: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 25px;
    margin-top: 20px;
`;

const Title = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 8px;
`;

const Description = styled.p`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    margin-bottom: 20px;
`;

const InfoBox = styled.div<{ isWarning?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 20px;

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const InfoText = styled.p`
    font-size: var(--font-size-body-3);
    color: var(--color-primary);
    margin: 0;
`;

const OptionsWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const OptionCard = styled.div<{ isSelected: boolean }>`
    border: 1px solid ${(props) => props.isSelected ? 'var(--color-alt)' : 'var(--color-alt-second)'};
    border-radius: var(--box-border-radius);
    padding: 20px 30px;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover {
        border-color: var(--color-alt);
    }
`;

const OptionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
`;

const RadioButton = styled.div<{ isSelected: boolean }>`
    width: 18px;
    height: 18px;
    border: 2px solid ${(props) => props.isSelected ? 'var(--color-alt)' : 'var(--color-primary-70)'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &::after {
        content: '';
        width: 8px;
        height: 8px;
        background: ${(props) => props.isSelected ? 'var(--color-alt)' : 'transparent'};
        border-radius: 50%;
    }
`;

const OptionTitle = styled.span`
    font-size: var(--font-size-body-2);
    font-weight: 600;
    color: var(--color-primary);
`;

const SliderContainer = styled.div<{ isDisabled: boolean }>`
    opacity: ${(props) => props.isDisabled ? 0.4 : 1};
    pointer-events: ${(props) => props.isDisabled ? 'none' : 'auto'};
`;

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 25px;

    @media (max-width: 768px) {
        button {
            width: 100%;
            justify-content: center;
        }
    }
`;

const formatPrice = (value: number) => {
    return value.toLocaleString('pl-PL');
};

const PriceRangeStep: React.FC = () => {
    const {
        valuationState,
        setSelectedPriceRange,
        submitPrediction,
        fetchPriceRange
    } = useContext(PropertyValuationContext);

    const { priceRangeResponse, selectedPriceRange, selectedReportUuid, isLoading } = valuationState;

    const [priceType, setPriceType] = useState<PriceSearchValue>(
        selectedPriceRange?.priceSearchValue || PriceSearchValue.CenaZaMetr
    );

    useEffect(() => {
        if (selectedPriceRange?.priceSearchValue) {
            setPriceType(selectedPriceRange.priceSearchValue);
        }
    }, [selectedReportUuid]);

    if (!priceRangeResponse || !selectedPriceRange) {
        return null;
    }

    const handlePriceTypeChange = (type: PriceSearchValue) => {
        setPriceType(type);
        setSelectedPriceRange({
            ...selectedPriceRange,
            priceSearchValue: type
        });
    };

    const handlePricePerMeterChange = (minValue: number, maxValue: number) => {
        setSelectedPriceRange({
            ...selectedPriceRange,
            minPricePerSquareMeter: minValue,
            maxPricePerSquareMeter: maxValue
        });
    };

    const handleTotalPriceChange = (minValue: number, maxValue: number) => {
        setSelectedPriceRange({
            ...selectedPriceRange,
            minPrice: minValue,
            maxPrice: maxValue
        });
    };

    const handleSubmit = () => {
        submitPrediction();
    };

    const isPricePerMeterSelected = priceType === PriceSearchValue.CenaZaMetr;
    const isTotalPriceSelected = priceType === PriceSearchValue.CenaZOgloszenia;

    return (
        <Container>
            <RefreshButtonRow>
                <RefreshButton type="button" onClick={fetchPriceRange} disabled={isLoading}>
                    <Icon icon={IconEnum.RELOAD} />
                    Odśwież przedziały
                </RefreshButton>
            </RefreshButtonRow>
            <Title>Wybierz przedział cenowy</Title>
            <Description>
                Wybierz typ ceny i dostosuj zakres na podstawie podobnych nieruchomości.
            </Description>

            <InfoBox isWarning={!priceRangeResponse.sameCondition}>
                <Icon icon={IconEnum.INFO} />
                <InfoText>
                    {priceRangeResponse.sameCondition
                        ? 'Prezentowane ceny są z zakresu stanu mieszkania podanego w panelu.'
                        : 'Prezentowane ceny są spoza zakresu stanu mieszkania podanego w panelu.'}
                </InfoText>
            </InfoBox>

            <OptionsWrapper>
                <OptionCard
                    isSelected={isPricePerMeterSelected}
                    onClick={() => handlePriceTypeChange(PriceSearchValue.CenaZaMetr)}
                >
                    <OptionHeader>
                        <RadioButton isSelected={isPricePerMeterSelected} />
                        <OptionTitle>Cena za m²</OptionTitle>
                    </OptionHeader>
                    <SliderContainer isDisabled={!isPricePerMeterSelected}>
                        <RangeSlider
                            min={priceRangeResponse.rawMinPricePerM2 || 0}
                            max={priceRangeResponse.rawMaxPricePerM2 || 0}
                            minValue={selectedPriceRange.minPricePerSquareMeter}
                            maxValue={selectedPriceRange.maxPricePerSquareMeter}
                            onChange={handlePricePerMeterChange}
                            formatValue={formatPrice}
                            suffix="zł/m²"
                            minLabel="Min"
                            maxLabel="Max"
                            step={100}
                            disabled={!isPricePerMeterSelected}
                        />
                    </SliderContainer>
                </OptionCard>

                <OptionCard
                    isSelected={isTotalPriceSelected}
                    onClick={() => handlePriceTypeChange(PriceSearchValue.CenaZOgloszenia)}
                >
                    <OptionHeader>
                        <RadioButton isSelected={isTotalPriceSelected} />
                        <OptionTitle>Cena całkowita</OptionTitle>
                    </OptionHeader>
                    <SliderContainer isDisabled={!isTotalPriceSelected}>
                        <RangeSlider
                            min={priceRangeResponse.rawMinPrice || 0}
                            max={priceRangeResponse.rawMaxPrice || 0}
                            minValue={selectedPriceRange.minPrice}
                            maxValue={selectedPriceRange.maxPrice}
                            onChange={handleTotalPriceChange}
                            formatValue={formatPrice}
                            suffix="zł"
                            minLabel="Min"
                            maxLabel="Max"
                            step={1000}
                            disabled={!isTotalPriceSelected}
                        />
                    </SliderContainer>
                </OptionCard>
            </OptionsWrapper>

            <ButtonsWrapper>
                <Button onClick={handleSubmit}>
                    Generuj wycenę
                </Button>
            </ButtonsWrapper>
        </Container>
    );
};

export default PriceRangeStep;
